<template>
  <Sheet>
    <Sheet.Body class="px-0!">
      <template v-if="props.items.length">
        <template v-if="canRegister">
          <Sheet.MenuItem
            :icon="GlobeAddIcon"
            :disabled="anyInactive"
            @click="handleRegisterCountriesClick"
          >
            Register in more countries
            <template #description>
              Recognized and registered under local laws
            </template>
          </Sheet.MenuItem>

          <Sheet.MenuSeparator />
        </template>

        <Sheet.MenuItem :icon="MoveIcon" @click="handleMoveClick">
          Move
        </Sheet.MenuItem>

        <template v-if="areSelectedItemsFiles">
          <Sheet.MenuItem :icon="LockIcon" @click="handlePrivacyControlsClick">
            {{ privacyControlsCopy }}
          </Sheet.MenuItem>

          <Sheet.MenuItem
            :icon="ArchivedIcon"
            class="text-red-600"
            @click="handleArchiveClick"
          >
            Archive
          </Sheet.MenuItem>
        </template>
      </template>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Sheet, useSheet } from '@dacr/library'
import { MoveIcon, LockIcon, ArchivedIcon, GlobeAddIcon } from '@dacr/icons'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { useBillingStore } from '@/features/billing/store'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import {
  CopyrightStatus,
  FileRole,
  type Item,
} from '@/shared/graphql/router/graphql'
import { isFile } from '@/features/explorer/utils/itemGuards'

const props = defineProps<{
  items: Item[]
}>()

const { close } = useSheet()

const areSelectedItemsFiles = computed(() => props.items.every(isFile))

const selectedFiles = computed(() => props.items.filter(isFile))

const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const billingStore = useBillingStore()

const canRegister = computed(
  () =>
    originFlagEnabled.value &&
    billingStore.isPro &&
    areSelectedItemsFiles.value &&
    selectedFiles.value.every((file) => file.role === FileRole.Owner)
)

const anyInactive = computed(() =>
  selectedFiles.value.some(
    (file) => file.copyright?.status === CopyrightStatus.Inactive
  )
)

const privacyControlsCopy = computed(() => {
  const allFilesPublic =
    selectedFiles.value.every((file) => !file.settings?.isPrivate) &&
    props.items.length > 1
  const allFilesPrivate =
    selectedFiles.value.every((file) => file.settings?.isPrivate) &&
    props.items.length > 1

  if (allFilesPublic) return 'Make files private'
  if (allFilesPrivate) return 'Make files public'
  return 'Privacy controls'
})

const handleArchiveClick = () => {
  close()
  openModal(Modals.ArchiveFiles, {
    props: {
      files: selectedFiles.value,
    },
  })
}

const handlePrivacyControlsClick = () => {
  close()

  if (props.items.length === 1 && isFile(props.items[0])) {
    return openDialog(Dialog.FilePrivacySettings, {
      props: { file: props.items[0] },
    })
  }

  const allFilesPublic = selectedFiles.value.every(
    (file) => !file.settings?.isPrivate
  )
  const allFilesPrivate = selectedFiles.value.every(
    (file) => file.settings?.isPrivate
  )

  if (allFilesPublic) {
    return openModal(Modals.MakeFilesPrivate, {
      props: { ids: selectedFiles.value.map((file) => file.id) },
    })
  }

  if (allFilesPrivate) {
    return openModal(Modals.MakeFilesPublic, {
      props: { ids: selectedFiles.value.map((file) => file.id) },
    })
  }

  openDialog(Dialog.UpdateFilePrivacy, {
    props: { ids: selectedFiles.value.map((file) => file.id) },
  })
}

const handleMoveClick = () => {
  close()
  openDialog(Dialog.MoveItems, {
    props: { ids: props.items.map((item) => item.id) },
  })
}

const handleRegisterCountriesClick = () => {
  close()
  openDialog(Dialog.RegisterCountries, {
    props: { files: selectedFiles.value },
  })
}
</script>
